/**
 * Measured wall card heights, cached by (id, width bucket) so a card
 * scrolled back into view, or a return to an earlier width, lays out without
 * a new measurement.
 *
 * - A card without a height at the current width is measured in the commit
 *   that mounts (or widens) it (`measureMounted`), before the browser
 *   paints: the masonry never shows a card on its estimate and then moves it.
 * - One ResizeObserver follows every mounted card for later changes (an
 *   image's load, an edit); those bump `version` at most once per frame.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** Below this a height change is sub-pixel noise. */
const HEIGHT_EPSILON = 0.5;

type MeasureRef = (element: HTMLElement | null) => void;

/** The measured heights; a new object whenever one of them changed. */
export interface WallMeasuredHeights {
  version: number;
  get: (id: string, bucket: string) => number | undefined;
}

export interface WallMeasure {
  heights: WallMeasuredHeights;
  /** A stable callback ref that measures the card `id`. */
  refFor: (id: string) => MeasureRef;
  /**
   * Measure the mounted cards without a height at the current width, from a
   * layout effect of the commit that mounted or widened them.
   */
  measureMounted: () => void;
  /** Forget the heights and refs of cards no longer on the wall. */
  prune: (live: ReadonlySet<string>) => void;
}

function cacheKey(id: string, bucket: string): string {
  return `${id}@${bucket}`;
}

/** `bucket` names the column width heights are cached under. */
export function useWallMeasure(bucket: string): WallMeasure {
  const [version, setVersion] = useState(0);
  const heightsRef = useRef(new Map<string, number>());
  const bucketRef = useRef(bucket);
  const idsRef = useRef(new Map<Element, string>());
  const elementsRef = useRef(new Map<string, HTMLElement>());
  const refsRef = useRef(new Map<string, MeasureRef>());
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef(0);

  useLayoutEffect(() => {
    bucketRef.current = bucket;
  }, [bucket]);

  const observer = useCallback((): ResizeObserver | null => {
    if (observerRef.current) return observerRef.current;
    if (typeof ResizeObserver === "undefined") return null;
    observerRef.current = new ResizeObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        const id = idsRef.current.get(entry.target);
        if (!id) continue;
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        const key = cacheKey(id, bucketRef.current);
        const cached = heightsRef.current.get(key);
        if (
          cached !== undefined &&
          Math.abs(cached - height) <= HEIGHT_EPSILON
        ) {
          continue;
        }
        heightsRef.current.set(key, height);
        changed = true;
      }
      if (!changed || frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0;
        setVersion((current) => current + 1);
      });
    });
    return observerRef.current;
  }, []);

  useEffect(() => {
    // Cards attached before a remount are observed again.
    const resizeObserver = observer();
    elementsRef.current.forEach((element) => resizeObserver?.observe(element));
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
  }, [observer]);

  const heights = useMemo(
    (): WallMeasuredHeights => ({
      version,
      get: (id, cacheBucket) =>
        heightsRef.current.get(cacheKey(id, cacheBucket)),
    }),
    [version],
  );

  // A card's callback keeps its identity until the card leaves the wall, so
  // a StrictMode detach/re-attach (null, then the same element) re-observes.
  const refFor = useCallback(
    (id: string): MeasureRef => {
      const known = refsRef.current.get(id);
      if (known) return known;
      const ref: MeasureRef = (element) => {
        const previous = elementsRef.current.get(id);
        if (previous === element) return;
        if (previous) {
          observerRef.current?.unobserve(previous);
          idsRef.current.delete(previous);
          elementsRef.current.delete(id);
        }
        if (!element) return;
        idsRef.current.set(element, id);
        elementsRef.current.set(id, element);
        observer()?.observe(element);
      };
      refsRef.current.set(id, ref);
      return ref;
    },
    [observer],
  );

  // Reading the heights forces one layout; a card with no box yet (not laid
  // out) is left to the observer.
  const measureMounted = useCallback(() => {
    let changed = false;
    for (const [id, element] of elementsRef.current) {
      const key = cacheKey(id, bucketRef.current);
      if (heightsRef.current.has(key)) continue;
      const height = element.offsetHeight;
      if (height <= 0) continue;
      heightsRef.current.set(key, height);
      changed = true;
    }
    if (changed) setVersion((current) => current + 1);
  }, []);

  const prune = useCallback((live: ReadonlySet<string>) => {
    for (const id of refsRef.current.keys()) {
      if (!live.has(id) && !elementsRef.current.has(id)) {
        refsRef.current.delete(id);
      }
    }
    for (const key of heightsRef.current.keys()) {
      if (!live.has(key.slice(0, key.lastIndexOf("@")))) {
        heightsRef.current.delete(key);
      }
    }
  }, []);

  return { heights, refFor, measureMounted, prune };
}
